import { cn } from '@/lib/utils';

export interface DropSlotProps {
  slotNo: number;
  isAssigning?: boolean;
  isDropTarget?: boolean;
  dropPreviewName?: string | null;
  dimmed?: boolean;
  isTapTarget?: boolean;
  onTapAssign?: () => void;
  title?: string;
  subtitle?: string;
}

export function DropSlot({
  slotNo,
  isAssigning = false,
  isDropTarget = false,
  dropPreviewName,
  dimmed = false,
  isTapTarget = false,
  onTapAssign,
  title = 'Available slot',
  subtitle = 'Drop comment here',
}: DropSlotProps) {
  const showPreview = isDropTarget && dropPreviewName;

  return (
    <div
      role={isTapTarget ? 'button' : undefined}
      tabIndex={isTapTarget ? 0 : undefined}
      data-slot={slotNo}
      onClick={isTapTarget ? onTapAssign : undefined}
      onKeyDown={
        isTapTarget
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onTapAssign?.();
              }
            }
          : undefined
      }
      className={cn(
        'box-border grid h-[72px] min-h-[72px] max-h-[72px] shrink-0 grid-cols-[44px,1fr] overflow-hidden border-b transition-all duration-150 sm:grid-cols-[52px,1fr] md:grid-cols-[60px,1fr]',
        isAssigning && 'opacity-80',
        isDropTarget
          ? 'border-blue-300 bg-blue-50 ring-2 ring-inset ring-blue-400'
          : dimmed
            ? 'border-slate-100 bg-slate-50/80 opacity-55'
            : 'border-slate-100 bg-white',
        isTapTarget && 'cursor-pointer touch-manipulation active:scale-[0.99]',
      )}
    >
      <div className="flex h-[72px] items-center justify-center border-r border-slate-100 px-1 sm:px-2 md:px-3">
        <span
          className={cn(
            'inline-flex h-6 w-6 items-center justify-center rounded-full sm:h-7 sm:w-7',
            'text-[11px] font-semibold shadow-sm sm:text-[12px]',
            isDropTarget ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700',
          )}
        >
          {slotNo}
        </span>
      </div>

      <div className="flex h-[72px] min-w-0 items-center gap-2 px-2 sm:gap-3 sm:px-3">
        <div
          className={cn(
            'flex shrink-0 items-center justify-center rounded-full p-1',
            isDropTarget ? 'bg-blue-100 text-blue-600' : 'text-slate-400',
          )}
        >
          <svg
            className="h-4 w-4 sm:h-5 sm:w-5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path d="M12 3v12m0 0l-4-4m4 4l4-4" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M5 21h14" strokeLinecap="round" />
          </svg>
        </div>

        <div className="min-w-0 flex-1">
          {showPreview ? (
            <>
              <span className="block truncate text-xs font-semibold text-blue-800 sm:text-sm">
                Assign {dropPreviewName}
              </span>
              <span className="block truncate text-[10px] text-blue-600 sm:text-[11px]">
                {isTapTarget
                  ? `Tap to create order in slot ${slotNo}`
                  : `Release to create order in slot ${slotNo}`}
              </span>
            </>
          ) : (
            <>
              <span className="block truncate text-xs font-semibold text-slate-500 sm:text-sm">
                {title}
              </span>
              <span className="block truncate text-[10px] text-slate-400 sm:text-[11px]">
                {subtitle}
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
